import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, ArrowRight,
  Clock, FileText, Calendar, Users, Scale, Zap,
  AlertTriangle, ClipboardList, MessageCircle, Folder,
  Shield, Lock,
} from 'lucide-react';
import BottomNav from '../components/BottomNav';
import DesktopSidebar from '../components/DesktopSidebar';

/**
 * AskStewieLanding — post-login home screen.
 * Matches the approved mockup: substation hero with Stewie mascot,
 * "What happened?" search bar (primary action), Popular Examples row,
 * 4 colored action tiles (Problem / Build / Ask / My Cases), trust footer,
 * fixed bottom navigation. Search submission routes to /ask-stewie?q=...
 * which auto-runs the structured-answer pipeline.
 */

const EXAMPLES = [
  { id: 'ot',         icon: Clock,          color: '#facc15', text: 'I was bypassed for overtime' },
  { id: 'corrective', icon: FileText,       color: '#ef4444', text: 'I received a corrective action' },
  { id: 'sched',      icon: Calendar,       color: '#a855f7', text: 'Management changed my schedule' },
  { id: 'ff',         icon: Users,          color: '#3b82f6', text: 'I was called into a fact finder' },
  { id: 'auth',       icon: Scale,          color: '#22c55e', text: 'Can management do this?' },
  { id: 'violation',  icon: Zap,            color: '#facc15', text: 'I think the contract was violated' },
];

const TILES = [
  {
    key: 'problem',
    Icon: AlertTriangle,
    color: '#dc2626',
    title: 'I HAVE A PROBLEM',
    desc: 'Discipline, investigations, harassment, overtime, scheduling, contracting, attendance, and more.',
    cta: 'GET HELP NOW',
    path: '/report-issue',
  },
  {
    key: 'build',
    Icon: ClipboardList,
    color: '#1d4ed8',
    title: 'BUILD MY CASE',
    desc: 'Fact finders, grievances, statements, timelines, information requests, and evidence gathering.',
    cta: 'BUILD NOW',
    path: '/fact-finder',
  },
  {
    key: 'ask',
    Icon: MessageCircle,
    color: '#15803d',
    title: 'ASK STEWIE',
    desc: 'Ask questions about your contract, rights, policies, overtime, benefits, seniority, and agreements.',
    cta: 'ASK A QUESTION',
    path: '/ask-stewie',
  },
  {
    key: 'cases',
    Icon: Folder,
    color: '#6d28d9',
    title: 'MY CASES',
    desc: 'View saved issues, grievances, investigations, timelines, and documents.',
    cta: 'VIEW MY CASES',
    path: '/case-locker',
  },
];

export default function AskStewieLanding({ user, onLogout }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const submitQuery = useCallback((q) => {
    const text = (q || query || '').trim();
    if (!text) return;
    navigate(`/ask-stewie?q=${encodeURIComponent(text)}`);
  }, [query, navigate]);

  function onSubmit(e) {
    e.preventDefault();
    submitQuery();
  }

  return (
    <div className="aslh-root" data-testid="askstewie-landing">
      <style>{styles}</style>
      <DesktopSidebar user={user} />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <header className="aslh-hero" data-testid="hero-section">
        <img
          src="/stewie_hero.png"
          alt="Ask Stewie — The Union Guy, powered by AI"
          className="aslh-hero-img"
          data-testid="stewie-mascot"
        />

        {/* Invisible click-targets layered over the painted icons in the artwork
            (hamburger top-left, bell top-right, profile top-right corner). */}
        <button
          type="button"
          className="aslh-hotspot aslh-hotspot--menu"
          data-testid="hero-menu-btn"
          onClick={() => navigate('/command-center')}
          aria-label="Menu"
        />
        <button
          type="button"
          className="aslh-hotspot aslh-hotspot--bell"
          data-testid="hero-notifications-btn"
          aria-label="Notifications (2 unread)"
          onClick={() => navigate('/notifications')}
        />
        <button
          type="button"
          className="aslh-hotspot aslh-hotspot--profile"
          data-testid="hero-profile-btn"
          aria-label="Profile"
          onClick={() => navigate('/profile')}
        />
      </header>

      {/* ── MAIN ─────────────────────────────────────────────────────────── */}
      <main className="aslh-main">

        {/* What happened? + search */}
        <section className="aslh-what" data-testid="search-section">
          <h1 className="aslh-q">
            What happened?
            <span className="aslh-q-underline" aria-hidden="true" />
          </h1>
          <form
            className="aslh-search-form"
            onSubmit={onSubmit}
            data-testid="landing-search-form"
          >
            <div className="aslh-search-wrap">
              <Search size={20} className="aslh-search-icon" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Tell Stewie what's going on..."
                className="aslh-search-input"
                data-testid="landing-search-input"
                autoComplete="off"
                enterKeyHint="search"
                inputMode="search"
              />
              <button
                type="submit"
                className="aslh-search-btn"
                disabled={!query.trim()}
                data-testid="landing-search-submit"
                aria-label="Ask Stewie"
              >
                <ArrowRight size={20} />
              </button>
            </div>
          </form>
          <div className="aslh-tagline">
            <Shield size={14} />
            <span>Your Rights. Your Contract. <strong>Your Voice.</strong></span>
          </div>
        </section>

        {/* Popular examples */}
        <section className="aslh-popular" data-testid="popular-examples">
          <h2 className="aslh-section-label">POPULAR EXAMPLES</h2>
          <div className="aslh-examples-row">
            {EXAMPLES.map((ex, i) => {
              const Icon = ex.icon;
              return (
                <button
                  key={ex.id}
                  type="button"
                  className="aslh-example-card"
                  onClick={() => submitQuery(ex.text)}
                  data-testid={`popular-example-${i}`}
                >
                  <span
                    className="aslh-example-icon"
                    style={{ color: ex.color, borderColor: `${ex.color}55` }}
                  >
                    <Icon size={20} strokeWidth={2.2} />
                  </span>
                  <span className="aslh-example-text">{ex.text}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* 4 tiles */}
        <section className="aslh-tiles" data-testid="quick-tiles">
          {TILES.map((t) => {
            const Icon = t.Icon;
            return (
              <button
                key={t.key}
                type="button"
                className="aslh-tile"
                onClick={() => navigate(t.path)}
                data-testid={`quick-tile-${t.key}`}
              >
                <div className="aslh-tile-head">
                  <span className="aslh-tile-icon" style={{ color: t.color }}>
                    <Icon size={42} strokeWidth={2.4} />
                  </span>
                  <h3 className="aslh-tile-title" style={{ color: t.color }}>{t.title}</h3>
                </div>
                <p className="aslh-tile-desc">{t.desc}</p>
                <div className="aslh-tile-cta" style={{ background: t.color }}>
                  <span>{t.cta}</span>
                  <ArrowRight size={16} strokeWidth={2.8} />
                </div>
              </button>
            );
          })}
        </section>

        {/* Trust footer */}
        <section className="aslh-trust" data-testid="trust-footer">
          <div className="aslh-trust-row">
            <Lock size={16} />
            <span>Everything shared with AskStewie is <strong>confidential and secure.</strong></span>
          </div>
          <div className="aslh-trust-row">
            <Lock size={16} />
            <span>Your information stays protected.</span>
          </div>
        </section>

        <div className="aslh-bottom-spacer" />
      </main>

      <BottomNav />
    </div>
  );
}

const styles = `
.aslh-root {
  min-height: 100vh;
  background: #0d1438;
  color: #f5f5f5;
  font-family: -apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", system-ui, sans-serif;
  padding-bottom: 80px;
}

/* ── HERO ───────────────────────────────────────────────────────────── */
.aslh-hero {
  position: relative;
  width: 100%;
  /* Native aspect ratio of /stewie_hero.png is 1024:480. Letting the image
     drive height keeps the artwork pixel-accurate at every viewport. */
  background: #050a1f;
  overflow: hidden;
}
.aslh-hero-img {
  display: block;
  width: 100%;
  height: auto;
}

/* Invisible click hotspots layered over the painted hamburger / bell /
   profile icons inside the hero artwork. */
.aslh-hotspot {
  position: absolute;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  border-radius: 12px;
  transition: background 0.15s;
}
.aslh-hotspot:hover { background: rgba(255,255,255,0.08); }
.aslh-hotspot:active { background: rgba(255,255,255,0.15); }
.aslh-hotspot:focus-visible {
  outline: 2px solid #facc15;
  outline-offset: 2px;
}
/* Positions are % of the 1024×480 hero, matching the painted icons. */
.aslh-hotspot--menu    { top: 3%;  left: 3%;  width: 9%;  height: 14%; }
.aslh-hotspot--bell    { top: 3%;  left: 84%; width: 7%;  height: 14%; }
.aslh-hotspot--profile { top: 3%;  left: 92%; width: 7%;  height: 14%; }

/* ── MAIN ───────────────────────────────────────────────────────────── */
.aslh-main {
  max-width: 1024px;
  margin: 0 auto;
  padding: 22px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 26px;
}

/* What happened ----------------------------------------------------- */
.aslh-what {
  display: flex; flex-direction: column; align-items: center;
  gap: 16px;
}
.aslh-q {
  position: relative;
  margin: 0;
  font-size: clamp(28px, 6.5vw, 38px);
  font-weight: 800;
  color: #ffffff;
  letter-spacing: -0.01em;
  text-align: center;
}
.aslh-q-underline {
  display: block;
  width: 78px; height: 4px;
  margin: 8px auto 0;
  background: #facc15;
  border-radius: 4px;
}
.aslh-search-form { width: 100%; }
.aslh-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
  background: #ffffff;
  border-radius: 22px;
  padding: 4px 4px 4px 18px;
  box-shadow: 0 6px 22px rgba(0,0,0,0.35);
}
.aslh-search-icon {
  color: #6b7280;
  flex-shrink: 0;
}
.aslh-search-input {
  flex: 1;
  background: none; border: none; outline: none;
  color: #0f172a;
  font-size: 16px;
  font-weight: 500;
  padding: 16px 12px;
  min-width: 0;
}
.aslh-search-input::placeholder { color: #94a3b8; font-weight: 500; }
.aslh-search-btn {
  flex-shrink: 0;
  width: 50px; height: 50px;
  border-radius: 50%;
  background: #1d4ed8;
  color: #ffffff;
  border: none;
  cursor: pointer;
  display: grid; place-items: center;
  transition: background 0.15s, transform 0.1s;
  box-shadow: 0 4px 14px rgba(29,78,216,0.45);
}
.aslh-search-btn:hover:not(:disabled) { background: #2563eb; }
.aslh-search-btn:active:not(:disabled) { transform: scale(0.94); }
.aslh-search-btn:disabled { background: #94a3b8; cursor: not-allowed; box-shadow: none; opacity: 0.6; }

.aslh-tagline {
  display: flex; align-items: center; justify-content: center;
  gap: 8px;
  color: #cbd5e1;
  font-size: 14px;
  font-weight: 500;
}
.aslh-tagline svg { color: #94a3b8; flex-shrink: 0; }
.aslh-tagline strong { color: #facc15; font-weight: 700; }

/* Popular examples -------------------------------------------------- */
.aslh-popular { display: flex; flex-direction: column; gap: 10px; }
.aslh-section-label {
  margin: 0; padding: 0 4px;
  font-size: 12px; font-weight: 800;
  letter-spacing: 0.18em;
  color: #cbd5e1;
}
.aslh-examples-row {
  display: flex; gap: 8px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding-bottom: 4px;
  margin: 0 -4px;
  padding-left: 4px; padding-right: 4px;
}
.aslh-examples-row::-webkit-scrollbar { display: none; }
.aslh-example-card {
  flex: 0 0 auto;
  scroll-snap-align: start;
  display: flex; align-items: center; gap: 10px;
  width: 168px;
  min-height: 76px;
  padding: 12px;
  background: rgba(15, 27, 70, 0.85);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 12px;
  color: #f1f5f9;
  cursor: pointer;
  text-align: left;
  transition: transform 0.12s, border-color 0.18s, background 0.18s;
}
.aslh-example-card:hover { border-color: rgba(250, 204, 21, 0.55); transform: translateY(-2px); }
.aslh-example-card:active { transform: translateY(0); }
.aslh-example-icon {
  flex-shrink: 0;
  width: 38px; height: 38px;
  border-radius: 50%;
  border: 1.5px solid;
  display: grid; place-items: center;
  background: rgba(0,0,0,0.25);
}
.aslh-example-text {
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.25;
  color: #f1f5f9;
}

/* 4 Tiles ----------------------------------------------------------- */
.aslh-tiles {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}
.aslh-tile {
  background: #ffffff;
  border: none;
  border-radius: 18px;
  padding: 18px 18px 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  cursor: pointer;
  text-align: left;
  box-shadow: 0 4px 14px rgba(0,0,0,0.25);
  transition: transform 0.14s, box-shadow 0.18s;
}
.aslh-tile:hover { transform: translateY(-3px); box-shadow: 0 8px 22px rgba(0,0,0,0.35); }
.aslh-tile:active { transform: translateY(-1px); }
.aslh-tile-head {
  display: flex; align-items: center; gap: 14px;
}
.aslh-tile-icon {
  flex-shrink: 0;
  width: 56px; height: 56px;
  display: grid; place-items: center;
}
.aslh-tile-title {
  margin: 0;
  font-size: 19px;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.15;
}
.aslh-tile-desc {
  margin: 0;
  color: #475569;
  font-size: 13.5px;
  line-height: 1.45;
  font-weight: 500;
}
.aslh-tile-cta {
  margin-top: auto;
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 14px;
  border-radius: 10px;
  color: #ffffff;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.04em;
}

/* Trust footer ------------------------------------------------------ */
.aslh-trust {
  display: flex; flex-direction: column; gap: 10px;
  background: rgba(13, 27, 75, 0.85);
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 14px;
  padding: 14px 16px;
}
.aslh-trust-row {
  display: flex; align-items: center; gap: 10px;
  font-size: 12.5px;
  color: #e2e8f0;
  line-height: 1.35;
}
.aslh-trust-row svg { flex-shrink: 0; color: #94a3b8; }
.aslh-trust-row strong { color: #facc15; font-weight: 700; }

.aslh-bottom-spacer { height: 16px; }

/* ── Tablet+ ───────────────────────────────────────────────────────── */
@media (min-width: 760px) {
  .aslh-tiles {
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
  .aslh-example-card { width: 192px; min-height: 84px; }
  .aslh-q { font-size: 38px; }
  .aslh-trust { flex-direction: row; justify-content: space-between; gap: 24px; padding: 16px 22px; }
  .aslh-trust-row { flex: 1; }
}
`;
